const { SURVEYTYPE_UNKNOWN } = require("../../../../../../commons/components/surveyloader/components/scanline/SurveyTypeDropdown/constants");
const TOKEN_UTIL = require("../../../../../../util/token.customerAppSettings/scanline");
const { sessionCustomerHasSurveyLoaderPermission, sessionIsSurveyLoader } = require("../../../../../../util/user");

const isDefined = v => v !== undefined && v !== null;


/** ***************
 * CONSTANTS
 */
const USER_IS_SURVEY_LOADER = !sessionCustomerHasSurveyLoaderPermission() || sessionIsSurveyLoader();

/** ***************
 * BEHAVIORS
 */

const NONE = 'NONE';
const TEMPORARY_ONLY = 'TEMPORARY_ONLY';
const TEMPORARY_FORCED = 'TEMPORARY_FORCED';
const TEMPORARY_ONLY_HIDDEN_SEARCHABLE = 'TEMPORARY_ONLY_HIDDEN_SEARCHABLE';
const TEMPORARY_ONLY_HIDDEN_SEARCHABLE_FORCE_SEARCHABLE = 'TEMPORARY_ONLY_HIDDEN_SEARCHABLE_FORCE_SEARCHABLE';
const SEARCHABLE_ONLY = 'SEARCHABLE_ONLY';
const SEARCHABLE_FORCED = 'SEARCHABLE_FORCED';
// const TEMPORARY_AND_SEARCHABLE = 'TEMPORARY_AND_SEARCHABLE';

const BEHAVIORS = {
	NONE,
	TEMPORARY_ONLY,
	TEMPORARY_FORCED,
	TEMPORARY_ONLY_HIDDEN_SEARCHABLE,
	TEMPORARY_ONLY_HIDDEN_SEARCHABLE_FORCE_SEARCHABLE,
	SEARCHABLE_ONLY,
	SEARCHABLE_FORCED,
}

const getFileUploadBehavior = ({ appSettingsOverride, hasSurveys, surveyType } = {}) => {
	const isUnknownSurvey = !isDefined(surveyType) || surveyType === 'UNKNOWN';
	const blnAllowTemporaryUploads = TOKEN_UTIL.blnAllowTemporaryUploads(appSettingsOverride);
	const blnAllowSearchableUploads = TOKEN_UTIL.blnAllowSearchableUploads(appSettingsOverride);

	
	if (blnAllowSearchableUploads && blnAllowTemporaryUploads) {
		if (USER_IS_SURVEY_LOADER) {
			if (isUnknownSurvey) {
				return TEMPORARY_ONLY;
			}
			return hasSurveys ? TEMPORARY_ONLY_HIDDEN_SEARCHABLE : TEMPORARY_ONLY_HIDDEN_SEARCHABLE_FORCE_SEARCHABLE;
		}
		return TEMPORARY_FORCED;
	}

	if (blnAllowSearchableUploads) {
		if (USER_IS_SURVEY_LOADER && !isUnknownSurvey) {			
			return hasSurveys ? SEARCHABLE_ONLY : SEARCHABLE_FORCED;
		}
		return NONE;
	}

	if (blnAllowTemporaryUploads) {
		if (USER_IS_SURVEY_LOADER && !isUnknownSurvey) {
			return TEMPORARY_ONLY;
		}
		return TEMPORARY_FORCED;
	}

  return NONE;
}


/** ***************
 * FILE BEHAVIOR FLAGS
 */

const BLN_TEMPORARY_UPLOAD = 'blnTemporaryUpload';
const BLN_SCAN_MAKE_SEARCHABLE = 'blnScanMakeSearchable';

const FILE_BEHAVIOR_FLAGS = {
	BLN_TEMPORARY_UPLOAD,
	BLN_SCAN_MAKE_SEARCHABLE,
}


/** ***************
 * UTIL
 */

/**
 * 
 * @param {*} fileObj 
 * @param {*} prevSurveyType - undefined, 'UNKNOWN', 'DEPOL', etc...
 * @param {*} currSurveyType -  undefined, 'UNKNOWN', 'DEPOL', etc...
 * @param {*} param3 
 * @returns 
 */

const computeDefaultFileBehaviorFlags = (fileObj, prevSurveyType, currSurveyType, { appSettingsOverride, hasSurveys } = {}) => {

	// SETTINGS
	const blnAllowTemporaryUploads = TOKEN_UTIL.blnAllowTemporaryUploads(appSettingsOverride);
	const blnAllowSearchableUploads = TOKEN_UTIL.blnAllowSearchableUploads(appSettingsOverride);
	const blnDefaultUploadsToSearchable = TOKEN_UTIL.blnDefaultUploadsToSearchable(appSettingsOverride);

	// SURVEY TYPE STATE
	const prevIsUnknown = !isDefined(prevSurveyType) || prevSurveyType.toUpperCase() === SURVEYTYPE_UNKNOWN;
	const currIsUnknown = !isDefined(currSurveyType) || currSurveyType.toUpperCase() === SURVEYTYPE_UNKNOWN;

	const shouldChangeToUnknownDefaults = !prevIsUnknown && currIsUnknown;
	const shouldChangeToKnownDefaults = prevIsUnknown && !currIsUnknown;

	const attemptTemporary = () => !!(blnAllowTemporaryUploads && hasSurveys);

	// CASE: no changes needed
	if (!shouldChangeToUnknownDefaults && !shouldChangeToKnownDefaults) {
		if (currIsUnknown) {
			return {
				[BLN_TEMPORARY_UPLOAD]: false,
				[BLN_SCAN_MAKE_SEARCHABLE]: false,
			}
		}

		// no need to modify flags
		return {
			[BLN_TEMPORARY_UPLOAD]: fileObj[BLN_TEMPORARY_UPLOAD],
			[BLN_SCAN_MAKE_SEARCHABLE]: fileObj[BLN_SCAN_MAKE_SEARCHABLE],
		}
	}

	const behavior = getFileUploadBehavior({ appSettingsOverride, hasSurveys, surveyType: currSurveyType });

	if (behavior === BEHAVIORS.NONE) {
		return {
			[BLN_TEMPORARY_UPLOAD]: false,
			[BLN_SCAN_MAKE_SEARCHABLE]: false,
		}
	}

	// CASE: return unknown survey type defaults
	if (shouldChangeToUnknownDefaults) {
		switch(behavior) {
			case BEHAVIORS.TEMPORARY_ONLY:
			case BEHAVIORS.TEMPORARY_FORCED: 
				return {
					[BLN_TEMPORARY_UPLOAD]: attemptTemporary(),
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
			case BEHAVIORS.TEMPORARY_ONLY_HIDDEN_SEARCHABLE: 
				return {
					[BLN_TEMPORARY_UPLOAD]: attemptTemporary(),
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
			case BEHAVIORS.SEARCHABLE_ONLY: 
				return {
					[BLN_TEMPORARY_UPLOAD]: false,
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
			default:
				return {
					[BLN_TEMPORARY_UPLOAD]: false,
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
		}
	}


	// CASE: return known survey type defaults
	if (shouldChangeToKnownDefaults) {
		switch(behavior) {
			case BEHAVIORS.TEMPORARY_FORCED: 
				return {
					[BLN_TEMPORARY_UPLOAD]: attemptTemporary(),
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
			case BEHAVIORS.TEMPORARY_ONLY_HIDDEN_SEARCHABLE: 
				return {
					[BLN_TEMPORARY_UPLOAD]: attemptTemporary(),
					[BLN_SCAN_MAKE_SEARCHABLE]: blnAllowSearchableUploads && !attemptTemporary(),
				}
			case BEHAVIORS.TEMPORARY_ONLY_HIDDEN_SEARCHABLE_FORCE_SEARCHABLE: 
				return {
					[BLN_TEMPORARY_UPLOAD]: false,
					[BLN_SCAN_MAKE_SEARCHABLE]: true,
				}			
			case BEHAVIORS.TEMPORARY_ONLY: 
				return {
					[BLN_TEMPORARY_UPLOAD]: attemptTemporary(),
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
			case BEHAVIORS.SEARCHABLE_ONLY: 
				return {
					[BLN_TEMPORARY_UPLOAD]: false,
					[BLN_SCAN_MAKE_SEARCHABLE]: blnAllowSearchableUploads && !!blnDefaultUploadsToSearchable,
				}
			default:
				return {
					[BLN_TEMPORARY_UPLOAD]: false,
					[BLN_SCAN_MAKE_SEARCHABLE]: false,
				}
		}
	}

	throw new Error('Error: computeDefaultFileBehaviorFlags, this line of code should not be reachable.')
}

export const UPLOAD_BEHAVIOR_UTIL = {
	BEHAVIORS,
	FILE_BEHAVIOR_FLAGS,
	getFileUploadBehavior,
	computeDefaultFileBehaviorFlags,
}

export default UPLOAD_BEHAVIOR_UTIL;

